import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  memo,
} from "react";
import { useForm, Controller, useWatch, useFormContext } from "react-hook-form";
import { Grid, Box, FormHelperText, CircularProgress } from "@mui/material";
import InputTextField from "@/Components/Form/InputTextField";
import DropdownField from "@/Components/Form/InputDropdown";
import CheckboxField from "@/Components/Form/CheckboxBox";
import DateSelector from "@/Components/Form/DateSelector";
import TextAreaField from "@/Components/Form/TextAreaField";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { hookMap } from "@/redux/hookMap";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import TypographyText from "@/Components/Form/TypographyText";
import { FileSelector } from "@/Components/Form/FileSelector";
import { ImageSelector } from "@/Components/Form/ImageSelector";
import CustomDateTimePicker from "@/Components/Form/DateTimePicker";
import { BackLink } from "@/Components/Buttons/Link";
import { FormHeader } from "@/Components/Form/FormHeader";
import RadioBoxField from "@/Components/Form/RadioBoxField ";
import Hr from "@/Components/Buttons/Hr";
import DateRangeSelector from "@/Components/Form/DateRangeCalendar";
import CardButton from "@/Components/Buttons/Card";
import SuccessMessage from "@/Components/Form/SuccessMessage";
import { FieldDynamicRules } from "@/Utils/Rules/FieldDynamicRules";
import { FieldCalculationRules } from "@/Utils//Rules/FieldCalculationRules";
import { useUser } from "@/Context/UserContext";
import { useFileUploadMutation } from "@/redux/ExportPage/CreateNewJob/Notes/NoteSlice";
import ToggleButton from "../../Components/Buttons/ToggleButton";
import { applyDropdownRules } from "../../Utils/Rules/applyDropdownRules";
import { FieldEnableRules } from "../../Utils/Rules/FieldEnableRules";
import FormLabel from "../../Components/Form/FormLabel";
import MultiSelectDropdown from "../../Components/Form/MultiSelectDropdown";
import { OrganizationHookMap } from "../../redux/OrganizationhookMap";
import { DynamicFieldRules } from "../../Utils/Rules/DynamicFieldRules";
import { DOCUMENT_FIELDS } from "../../Constant/Common/Common";
import IconToggle from "@/Components/Form/IconToggle";
// ==================== Field Wrapper ====================

const FieldWrapper = memo(
  ({
    field,
    control,
    autocompleteData,
    setValue,
    schemaFlat,
    manualDirtyFields,
    externalData,
    onSubmit,
    isFormEdited,
    onRequiredFieldsChange,
    dependentData,
    onChange,
    setLoading,
    currentValue,
    formName,
    resetField,
    initialData,
    isLastBlock,
    showRemove,
  }) => {
    // let depValue = undefined;
    let isDisabled = field.disabled ?? false;
    let isVisible = field.visible ?? true;
    let linkedValue;
    const mergedValues = { ...currentValue, ...externalData };
    const { setValueGlobal } = useUser();
    // let unDepValue = undefined;
    const depValue = useWatch({ control, name: field.dependsOn || "" });
    const unDepValue = useWatch({ control, name: field.unDependsOn || "" });
    let dropDownOptions = field.options || [];
    if (field.dependsOn && field.optionsMap && currentValue) {
      const dependsOn = currentValue[field.dependsOn];
      dropDownOptions = field.optionsMap[dependsOn] || [];
    }

    if (field.dependsOn || field.unDependsOn) {
      // Case 1: if dependsValue is defined → enable only if it matches
      if (Array.isArray(field.dependsValue) && field.dependsValue.length > 0) {
        isDisabled = !field.dependsValue.includes(depValue);
      } else if (
        Array.isArray(field.unDependsValue) &&
        field.unDependsValue.length > 0
      ) {
        isDisabled = field.unDependsValue.includes(unDepValue);
      }
      // Case 2: if only dependsOn → enable when it has any value
      else {
        isDisabled = !depValue;
        isDisabled = unDepValue;
      }
    }

    // ⚙️ Find rule(s) relevant to this field
    const applicableRules = FieldCalculationRules.filter(
      (rule) => rule.target === field.name
    );

    // 🧠 Watch only the dependencies of those rules (not the whole form)
    const watchFields =
      applicableRules.length > 0
        ? applicableRules.flatMap((rule) => rule.dependsOn)
        : [];

    const watchedValues = useWatch({
      control,
      name: watchFields,
    });

    useEffect(() => {
      if (!field.optionsMap) return;
      const currentFieldValue = control._formValues[field.name];
      if (!currentFieldValue) {
        return;
      }

      if (!dropDownOptions || dropDownOptions.length === 0) {
        setValue(field.name, "");
        if (manualDirtyFields.current) {
          delete manualDirtyFields.current[field.name];
        }
        return;
      }

      const isValid = dropDownOptions.some(
        (option) => option.value === currentFieldValue
      );
      if (!isValid) {
        setValue(field.name, "");
        if (manualDirtyFields.current) {
          delete manualDirtyFields.current[field.name];
        }
      }
    }, [
      dropDownOptions,
      field.name,
      setValue,
      control._formValues,
      manualDirtyFields,
    ]);

    const getValueByPath = (obj, path) => {
      return path?.split(".").reduce((acc, key) => acc?.[key], obj);
    };
    let apiParam;

    if (field.useLinkedValue || field.useParams) {
      const paramPath = field.useLinkedValue || field.useParams;

      apiParam =
        getValueByPath(mergedValues, paramPath)?.id ||
        getValueByPath(mergedValues, paramPath);
    } else {
      apiParam = undefined;
    }
    const useApiHook = field.endpoint
      ? OrganizationHookMap[field.endpoint] || hookMap[field.endpoint]
      : null;

    const hasLinkedValue = (() => {
      if (field.useParams) {
        const val = getValueByPath(mergedValues, field.useParams);
        return !!(val?.id || val || val === 0);
      }

      if (field.useLinkedValue) {
        const val = mergedValues?.[field.useLinkedValue];
        return !!(val?.id || val || val === 0);
      }

      return false;
    })();

    let apiResult = { data: [], isFetching: false };

    const buildApiParams = (field, apiParam) => {
      if (!field.useParams) return apiParam;
      return {
        [field.endpointWithParams]: apiParam,
      };
    };

    const finalParams = buildApiParams(field, apiParam);
    if (useApiHook) {
      apiResult = useApiHook(finalParams, {
        skip: (field.useLinkedValue || field.useParams) && !apiParam,
      });
    }

    const { data = [], isFetching, error } = apiResult;

    let dynamicData;

    if (error) {
      dynamicData = [{ label: "No Data Available", value: "" }];
    } else {
      dynamicData =
        Array.isArray(data) && data.length > 0
          ? data
          : [{ label: "No Data Available", value: "" }];
    }

    let isLoading = isFetching;

    if (field.dependsOn && !depValue) {
      isDisabled = true;
    }

    const getNestedValue = (obj, path) => {
      if (!obj || !path) return undefined;
      if (Array.isArray(path)) {
        const result = {};

        path.forEach((p) => {
          const normalizedPath = p.replace(/\[(\d+)\]/g, ".$1");

          const value = normalizedPath.split(".").reduce((acc, key) => {
            if (acc === undefined || acc === null) return undefined;

            if (Array.isArray(acc) && !isNaN(key)) {
              return acc[Number(key)];
            }

            if (Array.isArray(acc)) {
              const selectedItem = acc.find((item) => item?.selected) || acc[0];
              return selectedItem ? selectedItem[key] : undefined;
            }

            return acc?.[key];
          }, obj);

          const finalKey = normalizedPath.split(".").pop();
          result[finalKey] = value;
        });

        return result;
      }

      // ✅ SINGLE PATH (STRING)
      const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");

      return normalizedPath.split(".").reduce((acc, key) => {
        if (acc === undefined || acc === null) return undefined;

        if (Array.isArray(acc) && !isNaN(key)) {
          return acc[Number(key)];
        }

        if (Array.isArray(acc)) {
          const selectedItem = acc.find((item) => item?.selected) || acc[0];
          return selectedItem ? selectedItem[key] : undefined;
        }

        return acc?.[key];
      }, obj);
    };

    FieldDynamicRules.filter((rule) => rule.target === field.name).forEach(
      
      (rule) => {
        const depVal = getNestedValue(mergedValues, rule.dependsOn);
        const result = rule.condition?.(depVal);

        if (rule.property === "disabled") {
          isDisabled = result;
        }

        if (rule.property === "visible") {
          isVisible = result;

          if (result === false) {
            Promise.resolve().then(() => {
              setValue(rule.target, null, {
                shouldDirty: true,
                shouldValidate: true,
              });
            });

            manualDirtyFields.current[rule.target] = true;
          }
        }

        if (rule.property === "setValue") {
          const targetValue = result;

          const existingValue = currentValue?.[rule.target];

          // const isEmpty =
          //   existingValue === null ||
          //   existingValue === undefined ||
          //   existingValue === 0 || // ✅ IMPORTANT
          //   (typeof existingValue === "string" &&
          //     existingValue.trim() === "") ||
          //   (typeof existingValue === "object" &&
          //     Object.keys(existingValue).length === 0);
          const isEmpty =
            existingValue === null ||
            existingValue === undefined ||
            (typeof existingValue === "string" &&
              existingValue.trim() === "") ||
            (typeof existingValue === "object" &&
              Object.keys(existingValue).length === 0);

          if (!rule.overRide) {
            if (!isEmpty) {
              return;
            }
          }

          Promise.resolve().then(() => {
            if (targetValue === undefined || targetValue === null) {
              return; // ✅ STOP — do not overwrite existing value
            }

            setValue(rule.target, targetValue, { shouldValidate: true });
          });

          manualDirtyFields.current[rule.target] = true;
        }
        if (rule.property === "setOptions") {
          const targetField = schemaFlat.find((f) => f.name === rule.target);

          if (targetField) {
            targetField.options = result;

            const current = currentValue[rule.target];
            const allowedValues = (result || []).map((o) => o.value);
            if (!allowedValues.includes(current)) {
              Promise.resolve().then(() => {
                setValue(rule.target, "", { shouldValidate: true });
              });
            }
          } else {
            console.error(
              `❌ [TARGET FIELD NOT FOUND IN SCHEMA]: ${rule.target}`
            );
          }
        }

        // ✅✅✅ FORMULA RULE SUPPORT — NEW ADDITION (AUTO CALC)
        if (rule.formula) {
          const depValues = getNestedValue(mergedValues, rule.dependsOn);
          const formulaResult = rule.formula(depValues);
          Promise.resolve().then(() => {
            setValue(rule.target, formulaResult, { shouldValidate: true });
          });

          manualDirtyFields.current[rule.target] = true;
        }
      }
    );

    const valuesForVisibility = control._formValues || {};

    DynamicFieldRules.forEach((rule) => {
      if (rule.form === formName && rule.target === field.name) {
        const input = {};

        rule.dependsOn.forEach((dep) => {
          // CASE 1: Nested → kycDocument.docName
          if (dep.includes(".")) {
            const [parent, child] = dep.split(".");
            const fileObj =
              valuesForVisibility[parent]?.[child] ||
              initialData?.[parent]?.[child] ||
              null;

            input[dep] = fileObj;
          }

          // CASE 2: Checkbox field
          else {
            const value = valuesForVisibility[dep];

            if (typeof value === "object" && value !== null) {
              input[dep] = true;
            } else {
              input[dep] = value === true;
            }
          }
        });

        const visible = rule.formula(input);

        isVisible = visible;
      }
    });
    // ✅ Apply FieldEnableRules dynamically
    FieldEnableRules.filter((rule) => rule.target === field.name).forEach(
      (rule) => {
        // Check if the rule applies to this form
        if (
          !rule.form ||
          formName?.toLowerCase().includes(rule.form.toLowerCase())
        ) {
          const shouldEnable = rule.formula(currentValue || {});
          // Directly override disable state
          isDisabled = !shouldEnable;
        }
      }
    );

    useEffect(() => {
      if (applicableRules.length === 0) {
        return;
      }

      if (!Array.isArray(watchedValues)) {
        return;
      }

      applicableRules.forEach((rule) => {
        const values = {};
        let hasEmptyDependency = false;

        rule.dependsOn.forEach((dep, i) => {
          const val = getNestedValue(control._formValues, dep);
          values[dep.split(".").pop()] = val;

          if (val === "" || val === null || val === undefined) {
            hasEmptyDependency = true;
          }
        });
        if (hasEmptyDependency) {
          const oldValue = getNestedValue(control._formValues, rule.target);

          if (oldValue !== "" && oldValue !== null && oldValue !== undefined) {
            setValueGlobal(rule.target, "");

            if (manualDirtyFields?.current) {
              manualDirtyFields.current[rule.target] = true;
            }
          }

          return; // ⛔ Stop calculation
        }

        const newValue = rule.formula(values);
        const oldValue = getNestedValue(control._formValues, rule.target);
        if (
          newValue !== undefined &&
          !isNaN(newValue) &&
          newValue !== oldValue
        ) {
          let formattedValue = newValue;

          switch (field.dataType) {
            case "int":
              formattedValue = Number(parseInt(newValue));
              break;
            case "float":
            case "decimal":
              formattedValue = Number(parseFloat(newValue).toFixed(2));
              break;
            case "string":
              formattedValue = String(newValue);
              break;
            default:
              formattedValue = newValue;
          }
          setValue(rule.target, formattedValue, { showValidate: true });

          if (manualDirtyFields?.current) {
            manualDirtyFields.current[rule.target] = true;
          }
        }
      });
    }, [
      JSON.stringify(watchedValues),
      JSON.stringify(applicableRules),
      control._formValues,
    ]);

    const options = useMemo(() => {
      const source = hasLinkedValue
        ? dynamicData?.length
          ? dynamicData
          : []
        : autocompleteData || [];
      if (field.type === "autocomplete" && source.length) {
        return source.map((item, idx) => ({
          label:
            item[field.labelKey] ||
            item.name ||
            item.label ||
            item.StateName ||
            "",
          value: item[field.valueKey] || item.id || item.value || idx,
          raw: item,
          key: `${item[field.labelKey] || item.name || ""}-${item?.id?.id || idx}`,
        }));
      }
      return [];
    }, [
      dynamicData,
      autocompleteData,
      field.labelKey,
      field.valueKey,
      field.type,
    ]);

    const updateAutofillTargets = (val, field, isAutofill = false) => {
      if (!field?.autofillTargets) return;
      Object.entries(field.autofillTargets).forEach(
        ([targetField, keyInObject]) => {
          const targetFieldDef = schemaFlat.find((f) => f.name === targetField);
          if (!targetFieldDef) return;

          const targetValue = getNestedAutoCompleteValue(val, keyInObject);
          if (targetFieldDef?.component?.toLowerCase() === "dropdown") {
            targetFieldDef.options = Array.isArray(targetValue)
              ? targetValue.map((item) => ({
                  label: item.label || item.name || "",
                  value: item.value || item.id || "",
                  raw: item,
                }))
              : [];
            setValue(
              targetField,
              targetFieldDef.options.length
                ? targetFieldDef.options[0].value
                : "",
              { shouldDirty: false, shouldValidate: true } 
            );

            if (!val) {
              getLabelFromValue(null, targetFieldDef);
            }
          } else {
            // if the field is textfield then set it as string
            setValue(targetField, targetValue || "", {
              shouldDirty: false,
              shouldValidate: true,
            });
          }
        }
      );
    };

    const clearDependentLinkedFields = (
      clickedFieldName,
      schemaFlat,
      resetField
    ) => {
      if (!clickedFieldName || !Array.isArray(schemaFlat)) return;

      // 🔍 Find all fields that depend on the clicked field
      const dependentFields = schemaFlat?.filter(
        (f) => f.useLinkedValue === clickedFieldName
      );
      // 🧹 If no dependent fields, return early
      if (!dependentFields.length) return;

      dependentFields.forEach((field) => {
        // Clear this dependent field’s value
        resetField(field.name, { defaultValue: "" });

        // 🌀 Recursively clear deeper levels (like City → Area → SubArea)
        clearDependentLinkedFields(field.name, schemaFlat, resetField);
      });
    };

    const handleClearField = (fieldName) => {
      // Clear dependents recursively
      clearDependentLinkedFields(fieldName, schemaFlat, resetField);
      resetField(field.name, { defaultValue: "" });
    };
    // Helper: safely get nested value using path like "address[0].city"
    function getNestedAutoCompleteValue(obj, path) {
      if (!obj || !path) return "";
      const keys = path
        .replace(/\[(\w+)\]/g, ".$1") // convert [0] → .0
        .split(".")
        .filter(Boolean);

      function deepFind(currentObj, keyIndex = 0) {
        if (currentObj === undefined || currentObj === null) return "";

        if (keyIndex === keys.length) return currentObj;

        const currentKey = keys[keyIndex];

        if (typeof currentObj === "object" && currentKey in currentObj) {
          return deepFind(currentObj[currentKey], keyIndex + 1);
        }

        if (Array.isArray(currentObj)) {
          for (const item of currentObj) {
            const found = deepFind(item, keyIndex);
            if (found !== "" && found !== undefined) return found;
          }
        }

        if (typeof currentObj === "object") {
          for (const value of Object.values(currentObj)) {
            const found = deepFind(value, keyIndex);
            if (found !== "" && found !== undefined) return found;
          }
        }

        return "";
      }
      return deepFind(obj, 0);
    }

    const endpointHooks = {};

    schemaFlat.forEach((field) => {
      if (field.endPointWithId) {
        const autoHook = hookMap[field.endPointWithId];

        if (autoHook) {
          endpointHooks[field.endPointWithId] = autoHook();
        }
      }
    });

    const runAutofill = (val) => {
      let addr = null;
      // Pick selected address or default first
      const selectedAddressId = control._formValues?.addressId;
      if (selectedAddressId && Array.isArray(val.address)) {
        addr = val.address.find((a) => a.value === selectedAddressId);
      }

      if (!addr && Array.isArray(val.address) && val.address.length > 0) {
        addr = val.address[0];
      }

      if (!field.autofillTargets) return;

      Object.entries(field.autofillTargets).forEach(
        ([targetField, sourceKey]) => {
          const targetFieldDef = schemaFlat.find((f) => f.name === targetField);
          if (!targetFieldDef) return;
          const sourceValue =
            addr?.[sourceKey] !== undefined
              ? addr?.[sourceKey]
              : val?.[sourceKey];
          // Dropdown autofill
          if (targetFieldDef.component?.toLowerCase() === "dropdown") {
            const targetValue = Array.isArray(sourceValue)
              ? sourceValue
              : [sourceValue];

            targetFieldDef.options = targetValue.map((item) => ({
              label: item?.name || item?.label || "",
              value: item?.id || item?.value || "",
              raw: item,
            }));

            const prefilled = control._formValues?.[targetField];
            let finalValue = "";

            if (prefilled) {
              const match = targetFieldDef.options.find(
                (opt) =>
                  opt.value === prefilled?.id ||
                  opt.raw?.id === prefilled ||
                  opt.raw?.value === prefilled
              );
              if (match) finalValue = match.value;
            }

            if (!finalValue && targetFieldDef.options.length > 0) {
              finalValue = targetFieldDef.options[0].value;
            }
            manualDirtyFields.current[targetField] = true;
            setValue(targetField, finalValue, {
              shouldDirty: false,
              shouldValidate: true,
            });
          } else {
            // Skip if previous value already exists and is NOT empty
            const previousValue = control._formValues?.[targetField];
            if (previousValue && previousValue !== "") {
              return;
            }

            const resolved =
              typeof sourceValue === "object"
                ? sourceValue
                : (sourceValue ?? "");

            setValue(targetField, sourceValue, {
              shouldDirty: false,
              shouldValidate: true,
            });
          }
        }
      );
    };

    useEffect(() => {
      if (
        field.type !== "autocomplete" ||
        !Array.isArray(autocompleteData) ||
        autocompleteData.length === 0
      ) {
        return;
      }

      const selectedVal = control._formValues?.[field.name];
      if (!selectedVal) return;

      const selectedId =
        typeof selectedVal === "object"
          ? (selectedVal.id ?? selectedVal.value ?? "")
          : selectedVal;

      let val = autocompleteData.find(
        (item) =>
          item?.id?.id === selectedId ||
          item?.id === selectedId ||
          item?.value === selectedId ||
          item?.name === selectedId
      );

      if (!val) return;

      const hookInstance = endpointHooks[field.endPointWithId];
      if (!hookInstance) {
        runAutofill(val); // fallback: old logic
        return;
      }

      const { fetch } = hookInstance;
      if (!fetch) {
        runAutofill(val);
        return;
      }

      const load = async () => {
        try {
          const response = await fetch({ id: selectedId });
          const addressList = response || response?.data || []; // handle both formats
          if (Array.isArray(addressList?.address)) {
            val = { ...val, address: addressList?.address };
          }

          runAutofill(val);
        } catch (e) {
          runAutofill(val); // still autofill from basic val
        }
      };

      load();
    }, [autocompleteData, field, control._formValues?.[field.name]]);

    const updateAutofillTargetsWithAPI = async (val, field) => {
      const autoHookInstance = endpointHooks[field.endPointWithId];

      if (!autoHookInstance) {
        console.warn(`❌ No instance for: ${field.endPointWithId}`);
        return;
      }

      const { fetch } = autoHookInstance;

      const response = await fetch({ id: val?.value?.id });

      updateAutofillTargets(response, field, true);
    };

    const handleSelect = async (selected, isAutofill = false) => {
      setValue(field.name, selected?.value ?? selected?.id ?? selected ?? "", {
        shouldDirty: !isAutofill,
        shouldValidate: true,
      });

      if (!isAutofill) manualDirtyFields.current[field.name] = true;

      if (field.endPointWithId) {
        await updateAutofillTargetsWithAPI(selected, field);
      } else {
        updateAutofillTargets(selected, field, isAutofill);
      }
    };

    // When a dropdown changes if data needs to autoFill, this function will call the prefill function
    const getLabelFromValue = (val, field) => {
      if (!val) {
        updateAutofillTargets(null, field);
        return "";
      }
      if (field.type === "autocomplete" && field.autofillTargets) {
        updateAutofillTargets(val, field);
      }

      return val.label || val.name || "";
    };

    if (!isVisible) return null;

    const loading =
      field.type === "autocomplete" && field.endpoint && !autocompleteData;

    // switch case to show the type of components
    switch (field?.component?.toLowerCase()) {
      case "text-field":
        return (
          // controller component will handle the onchange process
          <Controller
            field={field}
            name={field.name}
            control={control}
            rules={{
              required: field.required ? "This field is required" : false,
              pattern: field.regex
                ? {
                    value: new RegExp(field.regex),
                    message: field.regexMessage || "Invalid format",
                  }
                : undefined,
            }}
            render={({ field: controllerField, fieldState }) => (
              <InputTextField
                type={field.type} // must pass  "phone"
                label={field.label}
                name={field.name}
                field={field}
                control={control}
                placeholder={field.placeholder}
                value={controllerField.value || field?.value || ""}
                onChange={(val) => {
                  controllerField.onChange(val);
                  manualDirtyFields.current[field.name] = true;

                  // Notify parent about live changes
                  if (typeof onChange === "function") {
                    onChange(field.name, val);
                  }

                  if (!isFormEdited.current) {
                    onRequiredFieldsChange?.(true);
                    isFormEdited.current = true;
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (typeof onChange === "function") {
                      onChange(field.name, val);
                    }

                    if (!isFormEdited.current) {
                      isFormEdited.current = true;
                      onRequiredFieldsChange?.(true);
                    }
                  }
                }}
                onSelectOption={handleSelect}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                width={field.width}
                defaultValue={field.defaultValue}
                editable={field.editable}
                disabled={isDisabled}
                afterText={field.afterText}
                gap={field.gap}
                sx={field.sx}
                required={field.required}
                dataType={field.dataType ?? "text"}
                loading={field.type === "autocomplete" ? isLoading : false}
                options={field.type === "autocomplete" ? options : undefined}
                autofillTargets={field.autofillTargets}
                updateAutofillTargets={updateAutofillTargets} // <-- pass it here
                icon={field.icon}
                clearDependentLinkedFields={handleClearField}
              />
            )}
          />
        );

      case "hr":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={null} // just to register the field
            render={() => <Hr key={field.name} />}
          />
        );

      case "dropdown":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              required: field.required ? "This field is required" : false,
              pattern: field.regex
                ? {
                    value: new RegExp(field.regex),
                    message: field.regexMessage || "Invalid format",
                  }
                : undefined,
            }}
            render={({ field: controllerField, fieldState }) => (
              <DropdownField
                label={field.label}
                options={field.options || []}
                value={controllerField.value ?? field.value ?? ""}
                defaultValue={field.value ?? ""}
                onChange={(val) => {
                  controllerField.onChange(val);
                  manualDirtyFields.current[field.name] = true;

                  applyDropdownRules(
                    formName, // dynamic form name passed from FormGenerator
                    field.name,
                    control._formValues,
                    setValue,
                    schemaFlat
                  );

                  onRequiredFieldsChange?.(true); // enable Save button
                  if (field.autofillTargets) {
                    handleSelect(val);
                  }

                  if (!isFormEdited.current) {
                    isFormEdited.current = true; // mark form as edited
                  }
                }}
                renderValue={(val) => getLabelFromValue(val, field)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                width={field.width}
                disabled={isDisabled}
                gap={field.gap}
                required={field.required}
                sx={field.sx}
                startIconName={field.startIconName}
                placeholder={field.placeholder}
              />
            )}
          />
        );

      case "textarea":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              required: field.required ? "This field is required" : false,
              pattern: field.regex
                ? {
                    value: new RegExp(field.regex),
                    message: field.regexMessage || "Invalid format",
                  }
                : undefined,
            }}
            render={({ field: controllerField, fieldState }) => (
              <TextAreaField
                label={field.label}
                actionText={field.actionText}
                value={controllerField.value || ""}
                defaultValue={field.defaultValue || ""}
                onChange={(val) => {
                  controllerField.onChange(val);
                  manualDirtyFields.current[field.name] = true;
                  if (!isFormEdited.current) {
                    onRequiredFieldsChange?.(true); // enable Save button
                    isFormEdited.current = true; // mark form as edited
                  }
                }}
                loading={field.type === "autocomplete" ? isLoading : false}
                options={field.type === "autocomplete" ? options : undefined}
                width={field.width || 200}
                helperText={fieldState.error?.message}
                error={!!fieldState.error}
                height={field.height || 100}
                disabled={isDisabled}
                sx={field.sx}
                placeholder={field.placeholder}
                required={field.required}
                rows={field.rows || 4}
                maxRows={field.maxRows || field.rows}
              />
            )}
          />
        );

      case "header":
      case "sectionheader":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.value || null} // optional, just to register the field
            render={() => (
              <FormHeader
                key={field.name}
                title={field.title || field.label || field.name}
              />
            )}
          />
        );

      case "backlink":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.value || null}
            render={() => (
              <BackLink
                key={field.name}
                gap={field.gap}
                label={field.label}
                action={field.action}
                disabled={isDisabled}
                sx={field?.sx}
                onClick={() => {
                  onSubmit({
                    action: field.action,
                    data: {
                      ...field,
                      subComponent: field.subComponent,
                    },
                  });
                }}
              />
            )}
          />
        );

      case "fileselector":
      case "file-selector":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField, fieldState }) => {
              const fileValue = controllerField.value || null;
              const [fileUpload] = useFileUploadMutation();

              return (
                <FileSelector
                  key={field.name}
                  fieldName={field.name}
                  gap={field.gap}
                  label={field.label}
                  value={fileValue}
                  onChange={async (fieldName, files) => {
                    if (!files) return;

                    const filesArray = Array.isArray(files) ? files : [files];
                    const token = localStorage.getItem("token");
                    const file = filesArray[0];

                    if (file instanceof File) {
                      try {
                        setLoading(true);
                        // 1. Upload file to file-upload API
                        const uploadResponse = await fileUpload({
                          file,
                          token,
                        }).unwrap();

                        // 2. Extract the file ID from response
                        let fileId = "";

                        if (typeof uploadResponse === "string") {
                          try {
                            // Parse the JSON string to get the object
                            const parsedResponse = JSON.parse(uploadResponse);

                            // Extract just the file ID
                            if (parsedResponse.exportNoteAttachmentId) {
                              fileId = parsedResponse.exportNoteAttachmentId;
                            } else if (parsedResponse.id) {
                              fileId = parsedResponse.id;
                            }
                          } catch (parseError) {
                            console.error(
                              "Failed to parse upload response as JSON:",
                              parseError
                            );
                          }
                        }
                        // If it's already an object
                        else if (
                          uploadResponse &&
                          typeof uploadResponse === "object"
                        ) {
                          if (uploadResponse.exportNoteAttachmentId) {
                            fileId = uploadResponse.exportNoteAttachmentId;
                          } else if (
                            uploadResponse.data?.exportNoteAttachmentId
                          ) {
                            fileId = uploadResponse.data.exportNoteAttachmentId;
                          } else if (uploadResponse.id) {
                            fileId = uploadResponse.id;
                          } else if (uploadResponse.data?.id) {
                            fileId = uploadResponse.data.id;
                          }
                        }

                        if (!fileId) {
                          console.error(
                            "Could not extract file ID from response"
                          );
                          return;
                        }

                        // 3. Store ONLY THE FILE ID STRING in form state
                        controllerField.onChange(fileId);

                        // Update form state
                        manualDirtyFields.current[field.name] = true;

                        if (!isFormEdited.current) {
                          onRequiredFieldsChange?.(true);
                          isFormEdited.current = true;
                        }
                      } catch (err) {
                        console.error(" File upload failed:", err);
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      console.log(
                        "⚠️ Not a File instance, probably already uploaded:",
                        file
                      );
                    }
                  }}
                  accept={field.accept}
                  multiple={field.multiple || false}
                  sx={field.sx}
                  width={field.width}
                  height={field?.height}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  allowedFileTypes={field.allowedFileTypes}
                  required={field.required}
                />
              );
            }}
          />
        );
      case "multiselect":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <MultiSelectDropdown
                label={field.label}
                placeholder={field.placeholder}
                value={controllerField.value || []}
                options={field.type === "autocomplete" ? options : undefined}
                onChange={(val) => {
                  controllerField.onChange(val);

                  if (typeof onChange === "function") {
                    onChange(field.name, val); // <-- this saves to formData
                  }

                  manualDirtyFields.current[field.name] = true;

                  if (!isFormEdited.current) {
                    onRequiredFieldsChange?.(true);
                    isFormEdited.current = true;
                  }
                }}
                width={field.width || 240}
                sx={field.sx}
                disabled={isDisabled}
              />
            )}
          />
        );

      case "image":
      case "file-upload":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.defaultValue || null}
            rules={{ required: field.required }}
            render={({ field: controllerField, fieldState }) => (
              <Box sx={{ ...field.sx }}>
                {/* Optional Label */}
                {field.formlabel && (
                  <FormLabel
                    label={field.formlabel}
                    required={field.required}
                    gap={1}
                  />
                )}

                {/* Image Selector */}
                <ImageSelector
                  label={field.formlabel}
                  value={controllerField.value}
                  onChange={(val) => {
                    controllerField.onChange(val);
                    manualDirtyFields.current[field.name] = true;
                    onRequiredFieldsChange?.(true);

                    if (!isFormEdited.current) {
                      isFormEdited.current = true;
                    }
                  }}
                  multiple={field.multiple}
                  accept={field.accept}
                  acceptLabel={field.acceptLabel}
                  width={field.width}
                  height={field.height}
                  sx={field.uploadSx}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required={field.required}
                />
              </Box>
            )}
          />
        );

      case "datetimeselector":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField, fieldState }) => (
              <CustomDateTimePicker
                key={field.name}
                gap={field.gap}
                label={field.label}
                value={controllerField.value || ""}
                onChange={(formatted, rawValue) => {
                  // Update RHF state
                  controllerField.onChange(formatted);
                  manualDirtyFields.current[field.name] = true;
                  // Call schema-level handler if provided
                  if (typeof field.onChange === "function") {
                    field.onChange(formatted, rawValue);
                  }
                  onRequiredFieldsChange?.(true); // enable Save button
                  if (!isFormEdited.current) {
                    isFormEdited.current = true; // mark form as edited
                  }
                }}
                sx={field.sx}
                textFieldSx={field.textFieldSx}
                width={field.width}
                disabled={field.disabled || isDisabled}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required={field.required}
                maxDateToToday={field.maxDateToToday}
              />
            )}
          />
        );

      case "checkboxbox":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <CheckboxField
                label={field.label}
                type={field.typeMode || "single"}
                checkboxLabel={field.checkboxLabel}
                options={field.options || []}
                value={
                  field.typeMode === "multi"
                    ? controllerField.value || []
                    : controllerField.value || false
                }
                onChange={(newValue) => {
                  controllerField.onChange(newValue);
                  manualDirtyFields.current[field.name] = true;
                  onRequiredFieldsChange?.(true); // enable Save button

                  if (!isFormEdited.current) {
                    isFormEdited.current = true; // mark form as edited
                  }
                }}
                enableUpload={field.enableUpload || false}
                disabled={isDisabled || field.disabled}
                width={
                  field.width ||
                  field.Width ||
                  field.sx?.width ||
                  field.sx?.Width
                }
                sx={field.sx}
              />
            )}
          />
        );

      case "radiobutton":
      case "radio-button":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.defaultValue || ""}
            rules={{ required: field.required }}
            render={({ field: controllerField, fieldState }) => (
              <Box sx={{ ...field.sx }}>
                {" "}
                {/* ✅ Apply your schema sx here */}
                {/* Group Label */}
                {field.formlabel && (
                  <FormLabel
                    label={field.formlabel}
                    required={field.required}
                    gap={1}
                  />
                )}
                {/* Radio Options */}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                  {field.options.map((option) => (
                    <RadioBoxField
                      key={`${field.name}-${option.value}`}
                      label={option.label}
                      name={controllerField.name}
                      value={option.value}
                      checked={controllerField.value === option.value}
                      onChange={(val) => {
                        controllerField.onChange(val);
                        manualDirtyFields.current[field.name] = true;
                        onRequiredFieldsChange?.(true);

                        if (!isFormEdited.current) {
                          isFormEdited.current = true;
                        }
                      }}
                    />
                  ))}
                </Box>
                {/* Group Helper/Error Message */}
                {fieldState.error && (
                  <FormHelperText sx={{ color: "var(--red-800)", mt: 0.5 }}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        );

      case "dateselector":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <DateSelector
                label={field.label}
                value={controllerField.value || null}
                onChange={(val) => {
                  controllerField.onChange(val);
                  manualDirtyFields.current[field.name] = true;
                  applyDropdownRules(
                    formName, // dynamic form name passed from FormGenerator
                    field.name,
                    control._formValues,
                    setValue,
                    schemaFlat
                  );
                  onRequiredFieldsChange?.(true); // enable Save button
                  if (!isFormEdited.current) {
                    isFormEdited.current = true; // mark form as edited
                  }
                }}
                disabled={isDisabled}
                required={field.required}
                width={field.width}
                gap={field.gap}
                maxDateToToday={field.maxDateToToday}
                maxDate={field.maxDate}
                minDate={field.minDate}
              />
            )}
          />
        );

      case "typographytext":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <TypographyText
                label={field.label}
                value={controllerField.value || field.value || ""}
                variant={field.variant || "bodySmall"}
                gap={field.gap}
                width={field.width}
                height={field.height}
                sx={field?.sx}
                actionText={field.actionText}
                actionTextColor={field.actionTextColor}
                actionTextSx={field.actionTextSx}
                icon={field.icon}
                iconPosition={field.iconPosition || "right"}
                iconColor={field.iconColor || "var--red-900"}
                loading={field.loading || false}
                disabled={isDisabled}
                onClick={() => {
                  field.onClick?.({
                    type: "action",
                    action: field.action,
                    data: controllerField.value,
                  });
                }}
              />
            )}
          />
        );

      case "daterangeselector":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.value || ""}
            render={({ field: controllerField }) => (
              <DateRangeSelector
                key={field.name}
                gap={field.gap}
                label={field.label}
                value={controllerField.value || ""}
                onChange={(formatted, rawValue) => {
                  controllerField.onChange(formatted); // update RHF
                  manualDirtyFields.current[field.name] = true;
                  // Call optional schema-level handler
                  if (typeof field.onChange === "function") {
                    field.onChange(formatted, rawValue);
                  }
                  onRequiredFieldsChange?.(true); // enable Save button
                  if (!isFormEdited.current) {
                    isFormEdited.current = true; // mark form as edited
                  }
                }}
                required={field.required}
                sx={field.sx}
                textFieldSx={field.textFieldSx}
                width={field.width}
                disabled={field.disabled || isDisabled}
                maxDateToToday={field.maxDateToToday}
              />
            )}
          />
        );

      case "togglebutton":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.value || false}
            render={({ field: controllerField }) => (
              <ToggleButton
                key={field.name}
                label={field.label}
                value={controllerField.value}
                disabled={field.disabled || isDisabled}
                onChange={(val) => controllerField.onChange(val)}
                required={field.required}
                gap={field.gap}
              />
            )}
          />
        );

      case "cardbutton":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.value || ""}
            render={({ field: controllerField }) => {
              // Safely handle both object and primitive values
              const fieldValue = controllerField.value;
              const value =
                typeof fieldValue === "object" && fieldValue !== null
                  ? (fieldValue.value ?? 0)
                  : (fieldValue ?? 0);

              const percentage =
                typeof fieldValue === "object" && fieldValue !== null
                  ? (fieldValue.percentage ?? 0)
                  : (field.percentage ?? 0);

              const arrowDirection =
                typeof fieldValue === "object" && fieldValue !== null
                  ? (fieldValue.arrowDirection ?? "Up")
                  : (field.arrowDirection ?? "Up");
              return (
                <CardButton
                  key={field.name}
                  label={field.label}
                  value={value}
                  percentage={percentage}
                  arrowDirection={arrowDirection}
                  caption={field.caption}
                  arrowColor={field.arrowColor}
                  borderColor={field.borderColor}
                />
              );
            }}
          />
        );

      case "successmessage":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            render={() => (
              <SuccessMessage
                key={field.name}
                label={field.label}
                icon={field.icon}
              />
            )}
          />
        );

      case "outlinebutton":
        return (
          <OutLinedButton
            variant={field.variant || "outlined"}
            label={field.text || field.label}
            iconName={field.iconName}
            iconSpacing={field.iconSpacing}
            type="button"
            width={field.width}
            height={field.height}
            sx={field?.sx}
            loading={field.loading || false}
            disabled={isDisabled}
            onClick={() => {
              onSubmit({
                action: field.action,
                data: field,
              });
            }}
          />
        );
      case "icon-toggle":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={field.defaultValue ?? false}
            render={({ field: controllerField }) => (
              <IconToggle
                value={controllerField.value}
                checkedIcon={field.checkedIcon}
                uncheckedIcon={field.uncheckedIcon}
                color={field.color}
                size={field.size}
                sx={field.sx}
                onChange={(val) => {
                  controllerField.onChange(val);
                  setValueGlobal(field.name, val);

                  manualDirtyFields.current[field.name] = true;

                  if (typeof onChange === "function") {
                    onChange(field.name, val); // pass live values up
                  }

                  if (!isFormEdited.current) {
                    isFormEdited.current = true;
                    onRequiredFieldsChange?.(true);
                  }
                }}
              />
            )}
          />
        );

      case "blackbutton":
        return (
          <BlackButton
            label={field.label}
            onClick={() => onSubmit({ action: field.action })}
            disabled={isDisabled}
            disableOnRequiredFields={field.disableOnRequiredFields}
            isRequiredFieldFilled={field.isRequiredFieldFilled}
          />
        );

      default:
        return null;
    }
  }
);

// ================= Form Generator =================
const FormGenerator = forwardRef(
  (
    {
      compKey,
      schema,
      onChange,
      onSubmit,
      initialData,
      selectedRow,
      showLoader = true,
      formName,
      onFieldValueChange,
      defaultValues,
      isLastBlock,
      showRemove,
    },
    ref
  ) => {
    const manualDirtyFields = useRef({});
    const isFormEdited = useRef(false);
    const {
      setIsRequiredFieldFilled,
      formData,
      isDataFetched,
      setLoading,
      setSetValueGlobal,
      setMarkDirtyExternal,
      loading,
    } = useUser();
    const schemaFlat = useMemo(() => {
      if (!schema || typeof schema !== "object") return []; // ✅ SAFETY

      const allFields = [];
      Object?.keys(schema)?.forEach((rowKey) => {
        if (rowKey.startsWith("row")) {
          const rowData = schema[rowKey];
          if (rowData && typeof rowData === "object") {
            Object.values(rowData).forEach((col) => {
              if (col && col.rows) {
                Object.values(col.rows).forEach((rowFields) => {
                  if (Array.isArray(rowFields)) {
                    allFields.push(...rowFields);
                  }
                });
              }
            });
          }
        }
      });

      return allFields;
    }, [schema]);

    const getNestedValue = (obj, path) => {
      if (!obj || !path) return undefined;

      const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1"); // ✅ [0] → .0

      return normalizedPath.split(".").reduce((acc, key) => acc?.[key], obj);
    };

    // 3️⃣ Setup RHF
    const initialValues = useMemo(() => {
      if (loading) return {};
      let valueFromFormData = formData?.[compKey];
      if (Array.isArray(valueFromFormData)) {
        const selected = valueFromFormData.find((row) => row.selected);

        valueFromFormData = selected || valueFromFormData[0] || null;
      }
      if (!valueFromFormData && compKey?.includes(".")) {
        valueFromFormData = getNestedValue(formData, compKey);
      }

      const base =
        defaultValues || initialData || valueFromFormData || formData || {};

      const kycDocument = {};
      DOCUMENT_FIELDS.forEach((doc) => {
        if (base[doc]) {
          kycDocument[doc] = base[doc];
        }
      });

      return {
        ...base,
        kycDocument,
      };
    }, [defaultValues, initialData, formData, compKey, loading]);

    const {
      control,
      handleSubmit,
      setValue,
      reset,
      getValues,
      watch,
      resetField,
      formState: { dirtyFields },
    } = useForm({
      defaultValues: initialValues,
      mode: "onChange",
    });

    useEffect(() => {
      if (initialValues == null) return;

      const keys = Object.keys(initialValues);

      if (keys.length === 0) {
        reset(
          {},
          {
            keepDirty: false,
            keepTouched: false,
            keepValues: false,
          }
        );
        return;
      }

      keys.forEach((key) => {
        setValue(key, initialValues[key], {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      });
    }, [initialValues]);

    const getMergedDirtyFields = () => {
      const merged = { ...dirtyFields };

      Object.keys(manualDirtyFields.current).forEach((k) => {
        merged[k] = true;
      });

      return merged;
    };

    useEffect(() => {
      setSetValueGlobal(setValue);
    }, [setValue]);

    useEffect(() => {
      setMarkDirtyExternal((fieldName) => {
        manualDirtyFields.current[fieldName] = true;
        setIsRequiredFieldFilled(true);
      });
    }, []);

    const prevFilledRef = useRef(null);

    const watchFieldName = useMemo(() => {
      const fieldToWatch = schemaFlat?.find((f) => f.isWatchField);
      return fieldToWatch?.name || null;
    }, [schemaFlat]);

    const watchedFieldValue = getValues()?.[watchFieldName];

    useEffect(() => {
      if (watchFieldName && typeof onFieldValueChange === "function") {
        onFieldValueChange(watchedFieldValue);
      }
    }, [watchedFieldValue, watchFieldName]);

    const values = getValues();

    const linkedFields = useMemo(
      () => schemaFlat.filter((field) => field.linkedValue),
      [schemaFlat]
    );

    const watchedLinkedValues = watch(linkedFields.map((f) => f.linkedValue));

    const dependentData = useMemo(() => {
      const map = {};
      linkedFields.forEach((field, index) => {
        map[field.name] = watchedLinkedValues[index];
      });
      return map;
    }, [watchedLinkedValues, linkedFields]);

    const endpoints = schemaFlat
      .filter((f) => f.endpoint)
      .map((f) => f.endpoint);

    const uniqueEndpoints = [...new Set(endpoints)];

    const autocompleteOptions = {};

    uniqueEndpoints.forEach((endpointName) => {
      const useApiHook = hookMap[endpointName];
      if (!useApiHook) return;
      const { data } = useApiHook();
      if (data) autocompleteOptions[endpointName] = data;
    });

    const filterAutocompleteOptions = {};
    const filterEndpoints = schemaFlat
      .filter((f) => f.filterEndPoint)
      .map((f) => f.filterEndPoint);
    const uniqueFilterEndpoints = [...new Set(filterEndpoints)];
    const filterFields = schemaFlat.filter((f) => f.filterEndPoint);

    const watchedFilterValues = watch(
      filterFields.map((f) => `${f.name}__search`)
    );
    filterFields.forEach((field, index) => {
      const searchText = watchedFilterValues?.[index] || "";

      const shouldFetch = searchText.trim().length >= 2;

      const filterApiHook = hookMap[field.filterEndPoint];

      if (!filterApiHook) {
        filterAutocompleteOptions[field.name] = [];
        return;
      }

      const { data: filteredData = [] } = filterApiHook(searchText, {
        skip: !shouldFetch,
      });

      filterAutocompleteOptions[field.name] = shouldFetch ? filteredData : [];
    });
    
    // 5️⃣ Expose submit to parent
    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise((resolve) => {
          handleSubmit((data) => {
            const updatedData = {};
            Object.keys(data).forEach((key) => {
              const fieldDef = schemaFlat.find((f) => f.name === key);
              if (!fieldDef) return;
              if (manualDirtyFields.current[key] || fieldDef.alwaysSend) {
                updatedData[key] = data[key];
              }
              if (selectedRow) updatedData.selectedId = selectedRow;
            });
            resolve(updatedData);
          })();
        }),
      reset: () => {
        reset({}, { keepValues: false });
        manualDirtyFields.current = {};
        isFormEdited.current = false;
        setIsRequiredFieldFilled(false);
        prevFilledRef.current = null;
        isFormEdited.current = false;
      },
      getCurrentValues: () => getValues(),
      setValue: (field, value) => {
        setValue(field, value, { shouldDirty: false });
      },
      getDirtyFields: () => getMergedDirtyFields(),

      enableButton: () => {
        setIsRequiredFieldFilled(true);
      },
    }));

    const renderField = (field) => {
      let options = [];

      if (field.type === "autocomplete") {
        const defaultList = autocompleteOptions[field.endpoint] || [];

        if (field.filterEndPoint) {
          const filteredList = filterAutocompleteOptions[field.name] || [];
          options = filteredList.length > 0 ? filteredList : defaultList;
        } else {
          options = defaultList;
        }
      }

      return (
        <FieldWrapper
          key={field.name}
          field={field}
          control={control}
          autocompleteData={options}
          setValue={setValue}
          schemaFlat={schemaFlat}
          manualDirtyFields={manualDirtyFields}
          externalData={formData}
          onChange={onChange}
          onSubmit={onSubmit}
          isFormEdited={isFormEdited}
          dependentData={dependentData}
          setLoading={setLoading}
          currentValue={values}
          initialData={initialData}
          resetField={resetField}
          showRemove={showRemove}
          onRequiredFieldsChange={(allFilled) =>
            setIsRequiredFieldFilled(allFilled)
          }
          formName={formName}
          isLastBlock={isLastBlock}
        />
      );
    };

    const allRows = useMemo(() => {
      if (!schema || typeof schema !== "object") return []; // ✅ SAFETY GUARD

      return Object.values(schema).flatMap((col) => {
        if (Array.isArray(col)) return col;
        if (col && col.rows) return Object.values(col.rows).flat();
        return [];
      });
    }, [schema]);

    if (showLoader && !isDataFetched) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            width: "100%",
            gap: 1,
          }}
        >
          <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {allRows.map((col, colIndex) => (
          <Box
            key={colIndex}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: col.columnWidth || "100%",
              border: col.highlight ? "1px solid #ccc" : "none",
              padding: col.highlight ? "16px" : "0",
              borderRadius: col.highlight ? "8px" : "0",
            }}
          >
            {Object.entries(col.rows).map(([rowKey, rowFields], rowIndex) => (
              <Box
                key={rowIndex}
                sx={{ display: "flex", gap: 2, mb: 1, width: "100%" }}
              >
                {rowFields.map(renderField)}
              </Box>
            ))}
          </Box>
        ))}
      </Grid>
    );
  }
);

export default FormGenerator;
